import { useCallback } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createPushSubscription,
  destroyPushSubscription,
  indexPushSubscriptions,
  pushSubscriptionQueryKeys,
  PushSubscriptionRow,
} from 'services/api/pushSubscriptions';

const NO_DEVICES: PushSubscriptionRow[] = [];

/**
 * The API's side of push for the signed-in user: every device they have registered, the server's
 * VAPID public key, and the two writes, register a device and remove one. Each write invalidates
 * the list so the devices and "this device" refresh from the server.
 */
export function usePushDevices({ active = true }: { active?: boolean } = {}) {
  const queryClient = useQueryClient();

  const index = useQuery({
    queryKey: pushSubscriptionQueryKeys.index(),
    queryFn: indexPushSubscriptions,
    enabled: active,
  });

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: pushSubscriptionQueryKeys.index() }),
    [queryClient]
  );

  const register = useMutation({ mutationFn: createPushSubscription, onSuccess: invalidate });
  const remove = useMutation({ mutationFn: destroyPushSubscription, onSuccess: invalidate });

  return {
    devices: index.data?.data ?? NO_DEVICES,
    vapidPublicKey: index.data?.meta.vapidPublicKey ?? null,
    isPending: index.isPending,
    isSuccess: index.isSuccess,
    // The index failing (403 when the flag is off server-side, 500, offline) is not "no keys".
    error: index.error,
    refetch: index.refetch,
    register,
    remove,
  };
}
